import { api } from './api.ts';

/**
 * What a call needs from the server, which is only the ICE servers.
 *
 * There is no call object to create and none to tear down: a call is two
 * browsers talking directly, and the only thing this client cannot work out for
 * itself is which STUN and TURN servers to use — because a TURN password is a
 * secret and must never be in this bundle.
 */
export interface IceConfig {
  iceServers: RTCIceServer[];
  /**
   * Whether a relay is available.
   *
   * Told plainly rather than inferred from the list, so the interface can say
   * *why* a call failed. Without a relay, two people both behind symmetric NAT
   * — most mobile carriers — cannot connect at all, and "we have no relay" is a
   * better thing to show than a spinner that never stops.
   */
  relayAvailable: boolean;
}

/**
 * Fetched once and kept for the session.
 *
 * The servers do not change between calls, and a request per call would add a
 * round trip to the one moment that is most sensitive to delay — the caller is
 * waiting, and until this returns nothing is ringing. A reload re-fetches,
 * which is often enough for a configuration change.
 */
let cached: Promise<IceConfig> | null = null;

export function iceConfig(): Promise<IceConfig> {
  cached ??= api.post<IceConfig>('/api/calls/ice').catch((error: unknown) => {
    // Not cached on failure: a call attempted during a blip should be able to
    // try again rather than inherit the failure for the rest of the session.
    cached = null;
    throw error;
  });
  return cached;
}

/** For tests, and for a sign-out that should not leak the previous account's. */
export function forgetIceConfig(): void {
  cached = null;
}
